import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  RadioGroup, Radio, Table, TableBody, TableCell, TableHead, TableRow,
  Typography, CircularProgress, Box, Snackbar, Alert, IconButton, Tooltip,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { SearchField } from '../../UI';
import { changeKeyHandler, KEY_NUMBER } from '../../../../helpers/utils/changeEvents.utils';
import {
  atualizarFavoritoService,
  buscarTextoPadraoPorCodigoService,
  buscarTextosPadraoDeAcordoComTiposService,
} from '../../../../services/textosPadrao/textosPadrao.services';

const MENSAGEM_ERRO_CARREGAR = 'Não foi possível carregar os textos padrão.';
const MENSAGEM_ERRO_FAVORITAR = 'Não foi possível atualizar o favorito.';
const TAMANHO_TRUNCAR = 80;

function truncar(texto) {
  const seguro = texto || '';
  return seguro.length > TAMANHO_TRUNCAR ? `${seguro.slice(0, TAMANHO_TRUNCAR)}…` : seguro;
}

/**
 * Sub-dialog "Texto Padrão" — Etapa 6 do plano `docs/architecture/
 * plano-aw2-1054-aba-historico.md`, revisado a pedido do usuário para
 * paridade visual/funcional com `TextoPadraoDialog.jsx` real: grid com
 * Número/Tipo/Texto, favoritar (coração à direita) e busca por código no
 * topo. Reaproveita os 3 services reais já auditados (nenhum endpoint
 * novo): `buscarTextosPadraoDeAcordoComTiposService` (filtro por tipo, list
 * inicial), `buscarTextoPadraoPorCodigoService` (busca), `atualizarFavoritoService`
 * (toggle de favorito + refetch, mesmo padrão da tela real).
 *
 * Diferença deliberada da tela real: mantém a callback única `onConfirmar
 * (texto)` (padrão já usado por `EmailsPadraoDialog.jsx`) em vez das
 * callbacks moldadas em `FieldModel`, e seleciona por `idTextoPadrao`
 * (identificador único e estável) em vez de índice do array ou chave
 * composta — a lista pode ser refeita a qualquer momento (pesquisa por
 * código, refetch pós-favoritar), então um índice ficaria dessincronizado
 * da linha certa assim que a ordem/composição da lista mudasse. Com seleção
 * por id, uma pesquisa nova que não contenha mais o item selecionado apenas
 * desmarca o radio e desabilita "Confirmar" naturalmente, sem código extra.
 *
 * Guard de requisição por contador monotônico (`requestRef`), não um
 * `ignore` local por efeito — porque a mesma função `carregarTextos` é
 * chamada por 3 gatilhos concorrentes possíveis (abrir o dialog, clicar
 * "Pesquisar", favoritar/desfavoritar um item), e uma resposta tardia de
 * qualquer um deles não pode sobrescrever o resultado de uma chamada mais
 * recente disparada por outro gatilho.
 */
export default function DialogTextoPadrao({
  open, tipos, onClose, onConfirmar,
}) {
  const [carregando, setCarregando] = useState(false);
  const [textos, setTextos] = useState([]);
  const [selecionado, setSelecionado] = useState('');
  const [codigoPesquisa, setCodigoPesquisa] = useState('');
  const [erro, setErro] = useState('');
  const [favoritandoIds, setFavoritandoIds] = useState(() => new Set());
  const requestRef = useRef(0);
  const favoritandoRef = useRef(new Set());

  const carregarTextos = useCallback((codigo) => {
    const idRequisicao = requestRef.current + 1;
    requestRef.current = idRequisicao;
    setCarregando(true);
    setErro('');
    const promise = codigo
      ? buscarTextoPadraoPorCodigoService(codigo)
      : buscarTextosPadraoDeAcordoComTiposService(tipos);
    return promise
      .then(({ data }) => { if (requestRef.current === idRequisicao) setTextos(data); })
      .catch(() => {
        if (requestRef.current === idRequisicao) { setTextos([]); setErro(MENSAGEM_ERRO_CARREGAR); }
      })
      .finally(() => { if (requestRef.current === idRequisicao) setCarregando(false); });
  }, [tipos]);

  useEffect(() => {
    if (!open) return;
    setSelecionado('');
    setCodigoPesquisa('');
    carregarTextos('');
  }, [open, carregarTextos]);

  function onChangeCodigoPesquisa(event) {
    changeKeyHandler(event, KEY_NUMBER);
    setCodigoPesquisa(event.target.value);
  }

  function onClickPesquisar() {
    carregarTextos(codigoPesquisa);
  }

  // Guard síncrono (`useRef`) contra duplo-clique: um `useState` sozinho só
  // reflete o novo valor no próximo render, e o `carregando` do refetch
  // ainda não liga nesse meio-tempo — 2 cliques na mesma linha antes do
  // primeiro PUT resolver enviariam o mesmo `favoritar`/`idTextoPadraoUsuario`
  // (ainda stale) duas vezes.
  async function onClickFavoritar(texto) {
    if (favoritandoRef.current.has(texto.idTextoPadrao)) return;
    favoritandoRef.current.add(texto.idTextoPadrao);
    setFavoritandoIds(new Set(favoritandoRef.current));
    try {
      await atualizarFavoritoService({
        favoritar: !texto.idTextoPadraoUsuario,
        idTextoPadraoUsuario: texto.idTextoPadraoUsuario,
        idTextoPadrao: texto.idTextoPadrao,
        idTipoTextoPadrao: texto.idTipoTextoPadrao,
      });
      await carregarTextos(codigoPesquisa);
    } catch {
      setErro(MENSAGEM_ERRO_FAVORITAR);
    } finally {
      favoritandoRef.current.delete(texto.idTextoPadrao);
      setFavoritandoIds(new Set(favoritandoRef.current));
    }
  }

  const textoSelecionado = textos.find(texto => String(texto.idTextoPadrao) === selecionado) || null;

  function onConfirmarClick() {
    if (!textoSelecionado) return;
    onConfirmar(textoSelecionado.texto);
    onClose();
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Textos Padrão</DialogTitle>
      <DialogContent>
        <SearchField
          placeholder="Número do Texto Padrão"
          value={codigoPesquisa}
          onChange={onChangeCodigoPesquisa}
          onSubmit={onClickPesquisar}
          loading={carregando}
          sx={{ mb: 2 }}
        />

        {carregando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <RadioGroup value={selecionado} onChange={event => setSelecionado(event.target.value)}>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 480 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40 }} />
                    <TableCell sx={{ fontWeight: 700 }}>Número</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Texto</TableCell>
                    <TableCell sx={{ width: 40 }} align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {textos.map(texto => (
                    <TableRow key={texto.idTextoPadrao}>
                      <TableCell>
                        <Radio size="small" value={String(texto.idTextoPadrao)} />
                      </TableCell>
                      <TableCell>{texto.idTextoPadrao}</TableCell>
                      <TableCell>{texto.descricaoTipoTextoPadrao}</TableCell>
                      <TableCell>
                        <Tooltip title={texto.texto}>
                          <span>{truncar(texto.texto)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={texto.idTextoPadraoUsuario ? 'Remover dos favoritos' : 'Favoritar'}>
                          <span>
                            <IconButton
                              size="small"
                              aria-label={texto.idTextoPadraoUsuario ? 'Remover dos favoritos' : 'Favoritar'}
                              disabled={favoritandoIds.has(texto.idTextoPadrao)}
                              onClick={() => onClickFavoritar(texto)}
                            >
                              {texto.idTextoPadraoUsuario
                                ? <FavoriteIcon fontSize="small" color="error" />
                                : <FavoriteBorderIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {!textos.length && (
                <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>
                  Nenhum texto padrão disponível.
                </Typography>
              )}
            </Box>
          </RadioGroup>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" disabled={!textoSelecionado} onClick={onConfirmarClick}>Confirmar</Button>
      </DialogActions>

      <Snackbar
        open={Boolean(erro)}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => setErro('')}
      >
        <Alert severity="error" variant="filled" onClose={() => setErro('')}>
          {erro}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}

DialogTextoPadrao.propTypes = {
  open: PropTypes.bool.isRequired,
  tipos: PropTypes.arrayOf(PropTypes.string).isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirmar: PropTypes.func.isRequired,
};
